"use client";
import { useAppContext } from "@/providers/AppProvider";
import { IEducation } from "@/types";
import { motion } from "framer-motion";
import { School2 } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef } from "react";

const EducationTimeline = () => {
    const { educations } = useAppContext();
    const educationRef = useRef<HTMLElement | null>(null);

    const sortedEducation = educations?.sort((a, b) => {
        return (
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
        );
    });

    // Scrolling to section if url have hash
    useEffect(() => {
        const hash = window.location.hash;
        if (hash === "#education") {
            if (educationRef.current) {
                educationRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }
    }, []);

    return (
        <motion.section
            ref={educationRef}
            id="education"
            className="pt-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
                hidden: {},
                visible: {
                    transition: {
                        staggerChildren: 0.2,
                    },
                },
            }}
        >
            <motion.h1
                className="mb-4 text-2xl font-bold lg:text-3xl"
                variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                }}
            >
                <Link href="#education" className="flex items-center gap-4">
                    <School2 className="size-10 text-primary" />
                    Education
                </Link>
            </motion.h1>
            <div className="ml-14 flex flex-col items-start">
                {sortedEducation?.map((education, index) => (
                    <EducationTimelineItem
                        key={index}
                        education={education}
                        isFirstItem={index === 0}
                        isLastItem={index === sortedEducation?.length - 1}
                    />
                ))}
            </div>
        </motion.section>
    );
};

export default EducationTimeline;

const EducationTimelineItem = ({
    isFirstItem,
    education,
    isLastItem,
}: {
    isFirstItem: boolean;
    education: IEducation;
    isLastItem: boolean;
}) => {
    const {
        degree,
        fieldOfStudy,
        institution,
        startDate,
        endDate,
        achievements,
    } = education;

    const formattedStartDate = new Date(startDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
    });

    const formattedEndDate = endDate
        ? new Date(endDate).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
          })
        : "Present";

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, x: -20 },
                visible: { opacity: 1, x: 0 },
            }}
            transition={{ duration: 0.4 }}
            className={`relative ${!isLastItem ? "pb-8" : ""}`}
        >
            {isFirstItem && (
                <div className="absolute -top-4 left-0 h-1/5 w-[1px] transform bg-border-color" />
            )}
            {!isLastItem && (
                <div className="absolute left-0 top-2 h-full w-[1px] -translate-x-1/2 transform bg-border-color" />
            )}
            <div className="absolute left-0 top-2 h-3 w-3 -translate-x-1/2 transform rounded-full bg-primary" />
            <div className="ml-4">
                <h2 className="text-lg font-semibold">{degree}</h2>
                <p className="text-sm font-medium text-primary">
                    {`${institution} - ${fieldOfStudy} (${formattedStartDate} - ${formattedEndDate})`}
                </p>
                <ul className="mt-2 list-inside list-disc">
                    {String(achievements ?? "")
                        .split("\n")
                        .filter(Boolean)
                        ?.map((achievement, index) => (
                            <li key={index}>{achievement}</li>
                        ))}
                </ul>
            </div>
        </motion.div>
    );
};
